import React, { useEffect, useState, useCallback } from "react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function StatusPill({ status }) {
  const map = {
    paid: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
    pending: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
    unpaid: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
    expired: "bg-muted text-muted-foreground",
  };
  return (
    <span className={`inline-flex items-center rounded-sm px-2 py-0.5 font-mono text-[10px] uppercase ${map[status] || map.expired}`}>
      {status || "—"}
    </span>
  );
}

export default function AdminPayments() {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("all");

  const load = useCallback(async () => {
    const params = {};
    if (status !== "all") params.status = status;
    const { data } = await api.get("/admin/transactions", { params });
    setItems(data.items);
    setTotal(data.total);
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className="font-mono text-xs uppercase tracking-wider text-[#002FA7]">// {t("admin.nav.payments")}</div>
      <h1 className="mt-2 font-display text-2xl font-black tracking-tight">{t("admin.nav.payments")}</h1>
      <p className="font-mono text-xs text-muted-foreground">{total} total</p>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="rounded-sm h-10 w-44" data-testid="admin-tx-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-sm">
            <SelectItem value="all">{t("admin.payments.all")}</SelectItem>
            <SelectItem value="paid">{t("admin.payments.paid")}</SelectItem>
            <SelectItem value="pending">{t("admin.payments.pending")}</SelectItem>
            <SelectItem value="unpaid">{t("admin.payments.unpaid")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 border border-border bg-card">
        <div className="grid grid-cols-12 border-b border-border px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          <div className="col-span-3">{t("admin.payments.date")}</div>
          <div className="col-span-3">{t("admin.payments.user")}</div>
          <div className="col-span-2">{t("admin.payments.plan")}</div>
          <div className="col-span-2">{t("admin.payments.amount")}</div>
          <div className="col-span-2">{t("admin.payments.status")}</div>
        </div>
        {items === null && <div className="p-6 font-mono text-sm text-muted-foreground">{t("common.loading")}</div>}
        {items && items.length === 0 && (
          <div className="p-6 font-mono text-sm text-muted-foreground">{t("admin.payments.empty")}</div>
        )}
        {items?.map((tx) => (
          <div key={tx.id} className="grid grid-cols-12 items-center border-b border-border px-4 py-3 last:border-0 text-sm">
            <div className="col-span-3 min-w-0">
              <div className="font-mono text-xs">{new Date(tx.created_at).toLocaleString()}</div>
              <div className="truncate font-mono text-[10px] text-muted-foreground">
                {t("admin.payments.session")}: {tx.session_id?.slice(0, 18)}…
              </div>
            </div>
            <div className="col-span-3 min-w-0">
              <div className="truncate text-sm">{tx.user_name || "—"}</div>
              <div className="truncate font-mono text-[11px] text-muted-foreground">{tx.user_email || tx.user_id}</div>
            </div>
            <div className="col-span-2 font-mono text-xs uppercase">{tx.plan_id || "—"}</div>
            <div className="col-span-2 font-display font-bold">
              {Number(tx.amount).toFixed(2)} {tx.currency?.toUpperCase()}
            </div>
            <div className="col-span-2"><StatusPill status={tx.payment_status} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}
